import { observer } from 'mobx-react-lite';
import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useTransition } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner, Tabs } from '@ifs-gcp/components';
import type { ITemplateNode } from '@ifs-gcp/core';
import { isTabNode } from '@ifs-gcp/core/helpers';
import type { TabCardChild, TabPanelChild } from '@ifs-gcp/core/models';

import { PageCard } from '@/components/page-card';
import type { FactoryElementProps } from '@/features/audit/audit-elements/factory-element';
import { getTabCardProps } from '@/features/audit/audit-tabs/utils';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { useAuditStore } from '@/store/auditStore';
import { useRouterStore } from '@/store/routerStore';
import { getTranslation } from '@/utils/translations';

import styles from './tab-card.module.scss';

interface SubTabPanelChildProps {
  element: TabPanelChild;
}

export const TabPanelWithScrollRestoration = ({
  children,
  element,
}: PropsWithChildren<SubTabPanelChildProps>) => {
  const {
    props: { value },
  } = element;

  const getScrollableContainer = useCallback(() => {
    return document.querySelector('[data-scroll-id]');
  }, []);

  useScrollRestoration({
    getScrollableContainer,
    subTabValue: value,
  });

  return children;
};

interface TabsRootProps {
  element: ITemplateNode<TabCardChild>;
  FactoryElement: FC<FactoryElementProps>;
}

export const TabCard = observer(({ element, FactoryElement }: TabsRootProps) => {
  const {
    i18n: { language },
  } = useTranslation();

  const audit = useAuditStore((state) => state.audit)!;
  const tabCards = useRouterStore((state) => state.selectedTabCards);
  const setSelectedTabCard = useRouterStore((state) => state.setSelectedTabCard);

  const [cardRef] = useScrollIntoView(element.props.id);
  const [transitionPending, startTransition] = useTransition();

  const {
    props: { id: tabCardId },
    children,
  } = element;

  const {
    cardTitle: {
      props: { text: cardTitle },
      children: cardTitleChildren,
    },
    cardBody,
  } = getTabCardProps(children);

  const tabMenuItems = useMemo(
    () =>
      cardTitleChildren.reduce<ComponentProps<typeof Tabs>['items']>((acc, curr) => {
        if (!isTabNode(curr)) {
          return acc;
        }

        const {
          props: { value, name },
        } = curr;

        if (Array.isArray(name)) {
          const matchedTabName = name.find((tabName) =>
            audit.conditions.validate(tabName.visibilityCondition),
          );

          if (!matchedTabName) {
            return acc;
          }

          return [
            ...acc,
            {
              id: value,
              label: getTranslation(matchedTabName.text, language),
              icon: curr.children?.[0] ? <FactoryElement element={curr.children[0]} /> : undefined,
            },
          ];
        }

        return [
          ...acc,
          {
            id: value,
            label: getTranslation(name, language),
            icon: curr.children?.[0] ? <FactoryElement element={curr.children[0]} /> : undefined,
          },
        ];
      }, []),
    [cardTitleChildren, audit.conditions, language, FactoryElement],
  );

  const currentTabIndex = useMemo(() => {
    const currentTabCard = tabCards[tabCardId];

    if (tabCardId && currentTabCard) {
      const foundIndex = tabMenuItems.findIndex(({ id }) => id === currentTabCard);

      if (foundIndex !== -1) {
        return foundIndex;
      }
    }

    return 0;
  }, [tabCardId, tabMenuItems, tabCards]);

  const isModuleSubTab = 'extensionTemplateId' in element.props;

  const onNavigate = useCallback(
    (newIndex: number) => {
      if (tabCardId) {
        const routeEvent = () =>
          setSelectedTabCard(tabCardId, cardBody[newIndex].props.value || '');

        if (isModuleSubTab) {
          startTransition(() => {
            routeEvent();
          });
        } else {
          routeEvent();
        }
      }
    },
    [tabCardId, isModuleSubTab, cardBody],
  );

  useEffect(() => {
    const { before = [] } = cardBody[currentTabIndex]?.props ?? {};

    if (before.length) {
      startTransition(() => {
        audit.invokeLifeCycleAction(before);
      });
    }
  }, [currentTabIndex]);

  if (!cardBody[currentTabIndex]) {
    return null;
  }

  const {
    props: { noPaddingAll },
  } = cardBody[currentTabIndex];

  const getChildren = () => {
    if (transitionPending) {
      return <Spinner />;
    }

    const factoryChildren = cardBody[currentTabIndex].children.map((nextElement) => (
      <FactoryElement key={nextElement.props.id as string} element={nextElement} />
    ));

    if (isModuleSubTab) {
      return (
        <TabPanelWithScrollRestoration element={cardBody[currentTabIndex]}>
          {factoryChildren}
        </TabPanelWithScrollRestoration>
      );
    }

    return factoryChildren;
  };

  const content = (
    <PageCard
      noContentPadding={noPaddingAll}
      cardHeaderProps={{
        ...(cardTitle && {
          title: getTranslation(cardTitle, language),
        }),
        subheader: (
          <Tabs
            onNavigate={onNavigate}
            value={currentTabIndex}
            items={tabMenuItems}
            isActiveGroupMustNotBeHighlighted
          />
        ),
      }}
    >
      {getChildren()}
    </PageCard>
  );

  if (isModuleSubTab) {
    return (
      <div ref={cardRef} className={styles.moduleCardWrapper} data-module-tab>
        {content}
      </div>
    );
  }

  return (
    <div ref={cardRef} className={styles.cardWrapper}>
      {content}
    </div>
  );
});
